import clsx from "clsx";
import { FC, useEffect, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

import { groupAccessIdFromGroupId, studentAccessIdFromUserId } from "../../../shared/access-id";
import { createId } from "../../../shared/utils";
import { AISettings, DEFAULT_MODEL_FOR_ASSESSMENT_QUERY } from "../components/AISettings";

import {
  AIAssessmentComparisonType,
  formatAiComparisonDataForCSV,
  generateAiAssessmentsForAllAssessedSubmissions,
} from "./assessment-csv";

import { PageLink } from "components/materials/section/assessment/PageLink";
import {
  useFetchAssessmentBaseData,
  useFetchAssessmentData,
} from "components/materials/section/assessment/assessment-data";
import { AIModelType } from "components/server-types";
import { t } from "i18n/i18n";
import { DownloadCSVButton } from "mds/components/DownloadCSVButton";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { PersonSingleIcon, RobotAIIcon, TrashDeleteIcon } from "mds/icons";
import { store, storeApi, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectCurrentSectionId,
  selectEnabledSectionUsers,
  selectUserSelectedAIModel,
  submissionSelectors,
} from "store/selectors";
import { isGroupPage } from "utils/page-utils";
import { sectionOutcomesUrl } from "utils/urls";

const formatTitle = (date: string): string => {
  // Example: "Jun 25 AI Assessment" -> "jul_25_ai_assessment_2025-07-02T21_34_35.031Z.csv"
  return `${new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${t("assessment_dashboard.all.csv_title")}`;
};

type GenerateCSVButtonProps = {
  onClick: () => void;
  disabled: boolean;
};

const GenerateCSVButton: FC<GenerateCSVButtonProps> = ({ onClick, disabled }) => {
  return (
    <SquareButton
      className="w-[160px]"
      disabled={disabled}
      kind="tertiary"
      size="xs"
      onClick={onClick}
    >
      <RobotAIIcon />
      <div>Run test</div>
    </SquareButton>
  );
};

type AITestItemType = {
  id: string;
  loading: boolean;
  data: AIAssessmentComparisonType[];
  aiModel: AIModelType;
  date: string;
};

const LOCAL_STORAGE_KEYS = {
  SETTINGS: "ai-dashboard-settings-v2",
  ITEMS: "ai-dashboard-items-v2",
};

const getSectionSpecificKey = (key: string, sectionId: string | null) =>
  sectionId ? `${key}-${sectionId}` : key;

/**
 * This view allows generating CSV files for AI assessment data on the current section data.
 * For each generated assessment, it keeps track of the system prompt used and the result data,
 * allowing downloading the CSV file for each result.
 *
 * The system prompt can be modified and the assessment can be re-run.
 * The list of items also shows simple success metrics for each run.
 */
export const AIDashboardView: FC = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const [items, setItems] = useLocalStorage<AITestItemType[]>(
    getSectionSpecificKey(LOCAL_STORAGE_KEYS.ITEMS, sectionId),
    [],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const isAnyLoading = items.some((item) => item.loading);
  const selectedItem = items.find((item) => item.id === selectedId) || null;
  const userSelectedAIModel =
    useAppSelector(selectUserSelectedAIModel) || DEFAULT_MODEL_FOR_ASSESSMENT_QUERY;

  useFetchAssessmentBaseData();
  useFetchAssessmentData();

  useEffect(() => {
    if (sectionId) {
      storeApi.outcomes.list({});
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.page_outcomes.list({ section_id: sectionId });
      storeApi.page_group_users.list({ section_id: sectionId });
    }
  }, [sectionId]);

  const onClearAllItems = () => {
    const confirm = window.confirm("Are you sure you want to clear all items?");
    if (!confirm) {
      return;
    }
    setItems([]);
  };

  const onRunAssessment = async () => {
    const state = store.getState();
    const newItem: AITestItemType = {
      id: createId(),
      loading: true,
      data: [],
      aiModel: userSelectedAIModel,
      date: new Date().toISOString(),
    };
    const newItems = [...items, newItem];
    setItems([...newItems]);
    const result = await generateAiAssessmentsForAllAssessedSubmissions(state, userSelectedAIModel);
    newItem.loading = false;
    newItem.data = result;
    setItems([...newItems]);
  };

  return (
    <main className="mt-4">
      <div className="flex items-center justify-between">
        <h3>AI Dashboard</h3>
        <div className="flex items-center gap-2">
          <AISettings />
          <GenerateCSVButton disabled={isAnyLoading} onClick={onRunAssessment} />
          <SquareButton disabled={isAnyLoading} kind="tertiary" size="xs" onClick={onClearAllItems}>
            <TrashDeleteIcon />
            Clear Items
          </SquareButton>
        </div>
      </div>
      <div className="my-4">
        {items.map((item) => (
          <ItemHeader
            item={item}
            key={item.id}
            selected={!!selectedItem && selectedItem.id === item.id}
            onDelete={() => {
              // TODO: Localize this
              const confirm = window.confirm("Are you sure you want to delete this item?");
              if (!confirm) {
                return;
              }
              const newItems = items.filter((i) => i.id !== item.id);
              setItems(newItems);
            }}
            onSelect={() =>
              selectedItem && selectedItem.id === item.id
                ? setSelectedId(null)
                : setSelectedId(item.data?.length ? item.id : null)
            }
          />
        ))}
      </div>
      <div className="mt-8 overflow-auto">
        {selectedItem ? (
          <ItemTable item={selectedItem} />
        ) : (
          <div className="flex items-center justify-center p-6">
            {items.length > 0 ? (
              <div>Select an item to view the CSV</div>
            ) : (
              <div>Run a test to get started</div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

const ItemHeader: FC<{
  item: AITestItemType;
  onSelect: () => void;
  selected: boolean;
  onDelete: () => void;
}> = ({ item, onSelect, onDelete, selected }) => {
  if (!item || item.loading || !item.data?.length) {
    return (
      <div
        className={clsx("my-2 flex items-center justify-between border p-4 hover:bg-blue-tint-90")}
      >
        <div>Loading...</div>

        <SquareIconButton kind="caution" size="s" onClick={() => onDelete()}>
          <TrashDeleteIcon />
        </SquareIconButton>
      </div>
    );
  }

  return (
    <div
      className={clsx("my-2 cursor-pointer border p-4 hover:bg-blue-tint-90", {
        "border-blue-tint-20 bg-blue-tint-95": selected,
      })}
      onClick={() => onSelect()}
    >
      <div className="flex items-center justify-between">
        <h4>{new Date(item.date).toLocaleString()}</h4>
        <div className="flex items-center gap-2">
          <ScoreBar item={item} />
          <DownloadCSVButton
            csvData={formatAiComparisonDataForCSV(item.data)}
            kind="white"
            title={formatTitle(item.date)}
          />
          <SquareIconButton kind="caution" size="s" onClick={() => onDelete?.()}>
            <TrashDeleteIcon />
          </SquareIconButton>
        </div>
      </div>
    </div>
  );
};

const ScoreBar: FC<{ item: AITestItemType }> = ({ item }) => {
  const totalRowCount = item.data.length;
  const acceptedRows = item.data.filter((row) => !row.ai_refused);

  const getRowMatch = (within: number) =>
    acceptedRows.filter((row) => {
      const humanScores = row.outcomes.map((o) => o.score);
      const aiScores = row.outcomes.map((o) => o.ai_score);

      const match = humanScores.every((score, i) => {
        const hasHumanScore = typeof score === "number";
        const aiScore = aiScores[i];
        const hasAIScore = typeof aiScore === "number";
        if (!hasHumanScore && !hasAIScore) {
          return true;
        }
        if (hasHumanScore && hasAIScore) {
          return Math.abs(score - aiScore) <= within;
        }
        return false;
      });
      return match;
    }).length;

  // How many of the human scores match directly with the AI scores
  const withinZero = getRowMatch(0);

  // How many of the human scores are within one point of the AI scores
  const withinOne = getRowMatch(1);

  const withinZeroScore = Math.floor((withinZero / acceptedRows.length) * 100);
  const withinOneScore = Math.floor((withinOne / acceptedRows.length) * 100);
  const refusalRate = Math.floor(((totalRowCount - acceptedRows.length) / totalRowCount) * 100);
  const apiErrorRate = Math.floor(
    (item.data.filter((row) => row.ai_error && !row.ai_refused).length / totalRowCount) * 100,
  );

  return (
    <div className="flex items-center gap-1">
      <div className="body-xs">{withinZeroScore}% match</div>
      <div className="mx-1 h-4 border-l border-black-tint-70" />
      <div className="body-xs">{withinOneScore}% close</div>
      <div className="mx-1 h-4 border-l border-black-tint-70" />
      <div className="body-xs">{refusalRate}% refused</div>
      <div className="mx-1 h-4 border-l border-black-tint-70" />
      <div className="body-xs">{apiErrorRate}% API errors</div>
      <div className="mx-1 h-4 border-l border-black-tint-70" />
      <div className="body-xs">{item.aiModel}</div>
    </div>
  );
};

const ItemTable: FC<{ item: AITestItemType }> = ({ item }) => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const pages = useAppSelector(pageSelectors.selectAll);
  const submissions = useAppSelector(submissionSelectors.selectAll);
  const sectionUsers = useAppSelector(selectEnabledSectionUsers);
  if (!currentSectionId) {
    return null;
  }
  return (
    <div className="">
      <div className="flex items-center justify-between">
        <h3>Sample from {new Date(item.date).toLocaleString()}</h3>
        <ScoreBar item={item} />
      </div>
      {item.data.map((row) => {
        const page = pages.find((p) => p.id === row.page_id);
        const submission = submissions.find((s) => s.id === row.submission_id);
        const userId = sectionUsers.find((u) => u.id === submission?.section_user_id)?.user_id;
        if (!page || !submission || !userId) {
          return null;
        }
        const isGrouped = isGroupPage(page);
        const accessId = isGrouped
          ? submission.page_group_id
            ? groupAccessIdFromGroupId(submission.page_group_id)
            : null
          : studentAccessIdFromUserId(userId);
        return (
          <div className="my-2 border border-black-tint-70 p-2" key={row.submission_id}>
            <div className="body-xs mb-2 flex items-center gap-3">
              <PageLink page={page} />
              <div>{accessId}</div>
            </div>
            <div className="flex">
              {[true, false].map((isHuman) => (
                <div
                  className="body-xs flex shrink-[1] grow-[1] basis-1/2 flex-col gap-2 p-2"
                  key={String(isHuman)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {isHuman ? <PersonSingleIcon /> : <RobotAIIcon />}
                    {row.outcomes.map((o) => {
                      return (
                        <OutcomeChip
                          key={o.title}
                          score={isHuman ? o.score : o.ai_score}
                          title={o.title}
                          to={sectionOutcomesUrl(currentSectionId, o.section_outcome_id)}
                        />
                      );
                    })}
                    {page.max_points > 0 && (
                      <div>
                        {(isHuman ? row.points : row.ai_points) || "?"}/{page.max_points} points
                      </div>
                    )}
                  </div>
                  <div className="body-xs">
                    {isHuman
                      ? row.comment
                      : row.ai_error
                        ? `Couldn't assess: ${row.ai_error}`
                        : row.ai_comment}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
